import { memo } from 'react';
import {
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  Tooltip,
  TooltipProps,
} from '@mui/material';
import styled from 'styled-components';
import { Item, ItemWithOtherProps } from 'types';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%; /* Ensure consistent width handling */
`;

// Modern API (preferred)
export interface SingleValuePickerV2Props {
  items: (string | number)[];
  itemsHash: Record<string, ItemWithOtherProps>;
  item: string | number;
  onValueChange: (event: any) => void;
  inputLabel?: string;
  inputName?: string;
  fullWidth?: boolean;
  paddingTop?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  helperText?: string;
  className?: string;
  tooltip?: TooltipProps['title'];
}

// Legacy API (backward compatibility)
export interface SingleValuePickerLegacyProps {
  items?: Item[]; // Array of {label, value} objects
  item: Item; // Current selected item object
  onValueChange: (event: any) => void;
  inputLabel?: string;
  inputName?: string;
}

export type SingleValuePickerProps = SingleValuePickerV2Props | SingleValuePickerLegacyProps;

// Type guard to check if using legacy API
function isLegacyProps(props: SingleValuePickerProps): props is SingleValuePickerLegacyProps {
  return (
    'items' in props &&
    props.items !== undefined &&
    Array.isArray(props.items) &&
    props.items.length > 0 &&
    typeof props.items[0] === 'object' &&
    'label' in props.items[0]
  );
}

/**
 * Unified SingleValuePicker Component
 *
 * Consolidates:
 * - SingleValuePicker.tsx (legacy Item[] API)
 * - SingleValuePickerV2.tsx (modern itemsHash API)
 *
 * Supports both APIs for backward compatibility during migration.
 *
 * @example
 * // Modern API (preferred)
 * <SingleValuePicker
 *   items={[1, 2, 3]}
 *   itemsHash={{ 1: { label: "One", value: 1 }, 2: { label: "Two", value: 2 }, ... }}
 *   item={1}
 *   onValueChange={(e) => setValue(e.target.value)}
 *   inputLabel="Select Option"
 * />
 *
 * @example
 * // Legacy API (still supported)
 * <SingleValuePicker
 *   items={[{ label: "One", value: 1 }, { label: "Two", value: 2 }]}
 *   item={{ label: "One", value: 1 }}
 *   onValueChange={(e) => handleChange(e)}
 * />
 */
const SingleValuePicker = (props: SingleValuePickerProps) => {
  // Handle legacy API
  if (isLegacyProps(props)) {
    const { items = [], item, onValueChange, inputName, inputLabel = 'Day' } = props;

    return (
      <Container className="single-value-picker-container">
        <InputLabel shrink htmlFor="select-multiple-native">
          {inputLabel}
        </InputLabel>
        <Select
          name={inputName}
          value={item.value}
          onChange={onValueChange}
          renderValue={() => <div key={item.label}>{item.label}</div>}
        >
          {items.map((curr) => {
            // Convert boolean to string for MenuItem value (MUI requirement)
            const itemValue = typeof curr.value === 'boolean' ? String(curr.value) : curr.value;
            return (
              <MenuItem key={String(curr.value)} value={itemValue}>
                {curr.label}
              </MenuItem>
            );
          })}
        </Select>
      </Container>
    );
  }

  // Handle modern API
  const {
    items = [],
    item,
    itemsHash,
    onValueChange,
    inputName,
    inputLabel = '',
    fullWidth = false,
    paddingTop = '',
    style = {},
    disabled = false,
    helperText = '',
    className = '',
    tooltip = '',
  } = props;

  const { label } = itemsHash[item] || {};

  const picker = (
    <Container
      style={{ width: fullWidth ? '100%' : 'unset', paddingTop, ...style }}
      className={`single-value-picker-v2-container ${className}`}
    >
      {inputLabel && (
        <InputLabel shrink htmlFor={inputName}>
          {inputLabel}
        </InputLabel>
      )}
      <Select
        fullWidth={fullWidth}
        name={inputName}
        disabled={disabled}
        value={item}
        onChange={onValueChange}
        renderValue={() => <div key={label}>{label}</div>}
      >
        {items.map((curr) => {
          const { value, label: itemLabel } = itemsHash[curr] || {};
          return (
            <MenuItem key={String(value)} value={value}>
              {itemLabel}
            </MenuItem>
          );
        })}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </Container>
  );

  return tooltip ? (
    <Tooltip title={tooltip}>
      <span>{picker}</span>
    </Tooltip>
  ) : (
    picker
  );
};

export default memo(SingleValuePicker);
